import { useEffect, useRef } from 'react';

/** While someone is still playing, the leaderboard can change any moment. */
export const POLL_PLAYING_MS = 10_000;
/** Nobody playing now, but friends may still join while the challenge is open. */
export const POLL_IDLE_MS = 30_000;

/**
 * Calls `poll` every `delayMs` (null = stopped). It skips hidden tabs, where nobody
 * is looking, and polls at once when the tab comes back. Never two calls at a time:
 * on a bad connection a slow answer must not pile up requests.
 */
export function usePolling(poll: () => Promise<void>, delayMs: number | null): void {
  const latest = useRef(poll);
  useEffect(() => {
    latest.current = poll;
  });

  useEffect(() => {
    if (delayMs == null) return;
    let busy = false;
    const run = () => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      void latest.current().finally(() => {
        busy = false;
      });
    };
    const id = setInterval(run, delayMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [delayMs]);
}

/** How often to refresh a leaderboard: faster while people play, never once closed. */
export function pollDelay(state: { closed: boolean; playing: number } | null): number | null {
  if (!state || state.closed) return null;
  return state.playing > 0 ? POLL_PLAYING_MS : POLL_IDLE_MS;
}
